import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@figma/fpl-components';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { I18N_KEYS } from '@/language/keys';

interface ToolDescriptionProps {
  description: string;
}

/** Shared description expansion for first-visit tool info and populated tool headers. */
export function ToolDescription({ description }: ToolDescriptionProps) {
  const { t } = useTranslation();
  const descriptionId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  return (
    <div {...stylex.props(styles.root)}>
      <Text.p
        id={descriptionId}
        variant="body-large"
        color="secondary"
        truncate={isExpanded ? undefined : 2}
        hideTooltip
        onTruncationChange={setHasOverflow}
        {...stylex.props(styles.description)}
      >
        {description}
      </Text.p>
      {hasOverflow && (
        <div {...stylex.props(styles.control)}>
          <Button.Link
            aria-expanded={isExpanded}
            aria-controls={descriptionId}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <Text.span variant="body-large" color="default">
              {t(isExpanded ? I18N_KEYS.TOOL_VIEW.VIEW_LESS : I18N_KEYS.TOOL_VIEW.VIEW_MORE)}
            </Text.span>
          </Button.Link>
        </div>
      )}
    </div>
  );
}

const styles = stylex.create({
  root: { display: 'flex', flexDirection: 'column', minWidth: 0, gap: spacer.spacer4px },
  description: { margin: 0, overflowWrap: 'anywhere' },
  control: { alignSelf: 'flex-start' },
});
